import {
  Power,
  UserCheck,
  Shield,
  ShieldCheck,
  Mail,
  MailOpen,
  Users,
  Search,
  CalendarDays,
  BarChart3,
  Bell,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react'
import {
  DEFAULT_SKILLS,
  SKILL_LABELS,
  type AgentConfig,
  type AgentSkills,
  type AgentRun,
} from '../api/agentService'

/* ─────────────────────────────── */
/*  Autonomy levels                */
/* ─────────────────────────────── */

export interface LevelDef {
  value: AgentConfig['autonomyLevel']
  label: string
  shortLabel: string
  icon: React.ElementType
  color: string
  bg: string
  border: string
  desc: string
  examples: string[]
}

export const LEVELS: LevelDef[] = [
  {
    value: 'disabled',
    label: 'Disabled',
    shortLabel: 'Off',
    icon: Power,
    color: 'text-gray-500',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    desc: 'Agent is completely inactive. No actions will be taken.',
    examples: ['No auto-replies', 'No suggestions', 'No background tasks'],
  },
  {
    value: 'assist',
    label: 'Assist Only',
    shortLabel: 'Assist',
    icon: UserCheck,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    desc: 'Generates drafts and suggestions, but never acts on its own.',
    examples: ['Drafts email replies', 'Suggests follow-ups', 'Prepares proposals'],
  },
  {
    value: 'human_in_loop',
    label: 'Human Review',
    shortLabel: 'Review',
    icon: Shield,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    desc: 'Handles low-risk tasks automatically. Asks for approval on anything important.',
    examples: ['Auto-sends welcome emails', 'Requests approval for refunds', 'Flags high-value actions'],
  },
  {
    value: 'autonomous',
    label: 'Autonomous',
    shortLabel: 'Auto',
    icon: ShieldCheck,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    desc: 'Operates independently with full logging. All actions are recorded for review.',
    examples: ['Full CRM automation', 'Auto-scheduling', 'Complete email pipeline'],
  },
]

export const LEVEL_INDEX: Record<string, number> = {
  disabled: 0,
  assist: 1,
  human_in_loop: 2,
  autonomous: 3,
}

/* ─────────────────────────────── */
/*  Skill icons                    */
/* ─────────────────────────────── */

export const SKILL_ICONS: Record<keyof AgentSkills, React.ElementType> = {
  sendEmails: Mail,
  readEmails: MailOpen,
  modifyCrm: Users,
  readCrm: Search,
  scheduleSessions: CalendarDays,
  generateReports: BarChart3,
  sendNotifications: Bell,
}

/* ─────────────────────────────── */
/*  Run table helpers              */
/* ─────────────────────────────── */

export const STATUS_STYLES: Record<string, { badge: string; icon: React.ElementType }> = {
  pending: { badge: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  completed: { badge: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle2 },
  approved: { badge: 'bg-green-100 text-green-700 border-green-200', icon: ShieldCheck },
  rejected: { badge: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  failed: { badge: 'bg-gray-100 text-gray-700 border-gray-200', icon: AlertTriangle },
}

export const RISK_STYLES: Record<string, string> = {
  low: 'text-green-600',
  medium: 'text-blue-600',
  high: 'text-amber-600',
  critical: 'text-red-600',
}

/* ─────────────────────────────── */
/*  Helpers for config JSON        */
/* ─────────────────────────────── */

export function getSkills(config: Record<string, unknown>): AgentSkills {
  const raw = config.skills as Partial<AgentSkills> | undefined
  return { ...DEFAULT_SKILLS, ...raw }
}

export function getRole(config: Record<string, unknown>): string {
  return (config.role as string) || ''
}

export function getSystemPrompt(config: Record<string, unknown>): string {
  return (config.system_prompt as string) || ''
}

export { DEFAULT_SKILLS, SKILL_LABELS }
export type { AgentConfig, AgentSkills, AgentRun }
